<script lang="ts" setup>
import SvgLoader from '@/utils/SvgLoader.vue';
import { onMounted, onUnmounted, reactive } from 'vue';
import { DropDownMenuItem } from './interfaces/dropDownItem';
import { i18n } from '@/i18n';

const t = i18n.t;

const props = defineProps<{
  userName: string;
  menuItems: DropDownMenuItem[];
}>();

const state = reactive({
  isOpen: false,
});

const toggleDropDown = () => {
  state.isOpen = !state.isOpen;
};

/**
 * ドロップダウンを閉じる
 * @param event - クリックイベント
 */
const closeDropdown = (event: MouseEvent) => {
  if (!(event.target as Element).closest(`#profile-menu-popover-dropdown`)) {
    state.isOpen = false;
  }
};

const onClickMenuItem = (item: DropDownMenuItem) => {
  item.action();
  state.isOpen = false;
};

onMounted(() => {
  document.addEventListener('click', closeDropdown);
});

onUnmounted(() => {
  document.removeEventListener('click', closeDropdown);
});
</script>

<template>
  <div id="profile-menu-popover-dropdown" class="profile-menu-container">
    <div class="profile-menu-container__icon" @click="toggleDropDown">
      <SvgLoader name="person" />
    </div>
    <div v-if="state.isOpen" class="profile-menu-container__popover-dropdown popover-dropdown">
      <div class="popover-dropdown__content">
        <div class="popover-dropdown__header dropdown-header">
          <div class="dropdown-header__content">
            <div class="dropdown-header__icon">
              <SvgLoader name="personProfileHeader" />
            </div>
            {{ userName }}
          </div>
        </div>
        <div class="popover-dropdown__body dropdown-body">
          <div v-for="item in menuItems" :key="item.label" class="dropdown-body__content-item-container" @click="onClickMenuItem(item)">
            <div class="dropdown-body__content-item">
              <SvgLoader v-if="item.icon" class="dropdown-body__icon" :name="item.icon" />
              {{ t(item.label) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.profile-menu-container {
  position: relative;

  &__icon {
    width: 40px;
    height: 40px;
    background-color: $semanticPrimary;
    border-radius: 50%;
    display: grid;
    place-items: center;
    &:hover {
      cursor: pointer;
    }
  }

  &__popover-dropdown {
    position: absolute;
    top: 0;
    left: calc(100% + 10px);
    width: 200px;
    max-height: 200px;
    z-index: 1000;
  }
}

.popover-dropdown {
  background-color: $semanticWhite;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  border-radius: 4px;
  overflow-y: scroll;
  font: Noto Sans JP;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0;
  color: $textDefault;
  text-align: left;
  overflow-wrap: break-word;

  &__content {
    display: grid;
    grid-auto-rows: max-content;
    gap: 8px;
    padding-top: 8px;
    padding-bottom: 8px;
    max-width: 200px;
  }

  &__header {
    width: 100%;
  }

  &__body {
    width: 100%;
  }
}

.dropdown-header {
  border-bottom: 1px solid $borderDefault;
  &__content {
    display: grid;
    grid-template-columns: 24px minmax(0, 1fr);
    justify-content: start;
    align-items: center;
    padding-top: 16px;
    padding-bottom: 16px;
    padding-left: 24px;
    padding-right: 16px;
    gap: 8px;
  }

  &__icon {
    width: 24px;
    height: 24px;
    background-color: $semanticPrimary;
    border-radius: 50%;
    display: grid;
    place-items: center;
  }
}

.dropdown-body {
  display: grid;
  grid-auto-rows: minmax(36px, max-content);
  gap: 8px;

  &__content-item-container {
    &:hover {
      cursor: pointer;
    }
  }

  &__content-item {
    display: grid;
    grid-auto-flow: column;
    justify-content: start;
    align-items: center;
    padding: 8px;
    gap: 4px;
  }

  &__icon {
    width: 20px;
    height: 20px;
  }

  &__label-text {
    grid-column: 2 / 3;
  }
}
</style>
